import {
  EXECUTION_MODES,
  OPERATION_STATES,
  isTerminalOperationState,
  normalizeProtocolOperationResponse,
} from "../api/schemas";

const PERSISTENCE_PREFIX = "carbon:protocolOperations:";
const stateOrder = OPERATION_STATES.reduce((acc, state, index) => {
  acc[state] = index;
  return acc;
}, {});

const SAFE_FIELDS = Object.freeze([
  "operationId",
  "operationType",
  "executionMode",
  "state",
  "status",
  "creationId",
  "semanticConfigHash",
  "chainId",
  "factoryAddress",
  "projectAddress",
  "receiptAddress",
  "settlementVaultAddress",
  "transactionHash",
  "blockNumber",
  "vendorStatus",
  "protocolPostconditionStatus",
  "postconditionStatus",
  "normalizedErrorCode",
  "normalizedErrorMessage",
  "applicationProjectionStatus",
  "metakeepTransactionId",
  "createdAt",
  "updatedAt",
  "submittedAt",
  "completedAt",
]);

export const createProtocolOperationsState = () => ({
  byId: {},
  order: [],
});

export const safeOperationSnapshot = (operation) => {
  const snapshot = {};
  for (const field of SAFE_FIELDS) {
    if (operation?.[field] !== undefined && operation[field] !== null) {
      snapshot[field] = operation[field];
    }
  }
  return snapshot;
};

export const upsertProtocolOperation = (state, rawOperation) => {
  const next = normalizeProtocolOperationResponse(rawOperation);
  const current = state.byId[next.operationId];
  if (current) {
    if (current.executionMode !== next.executionMode) {
      throw new Error("operation cannot switch execution mode");
    }
    if (current.state === "COMPLETED" && next.state !== "COMPLETED") {
      return current;
    }
    if (isTerminalOperationState(current.state) && !isTerminalOperationState(next.state)) {
      return current;
    }
    if (
      !isTerminalOperationState(current.state) &&
      stateOrder[next.state] < stateOrder[current.state]
    ) {
      throw new Error("operation state cannot move backward");
    }
  } else {
    state.order.push(next.operationId);
  }

  const merged = {
    ...(current ?? {}),
    ...safeOperationSnapshot(next),
  };
  state.byId[next.operationId] = merged;
  return merged;
};

const persistenceKey = (sessionKey) => `${PERSISTENCE_PREFIX}${sessionKey || "anonymous"}`;

export const persistOperationReferences = (storage, sessionKey, state) => {
  const references = state.order
    .map((operationId) => state.byId[operationId])
    .filter(Boolean)
    .map((operation) => ({
      operationId: operation.operationId,
      operationType: operation.operationType,
      state: operation.state,
      chainId: operation.chainId,
      transactionHash: operation.transactionHash,
      metakeepTransactionId: operation.metakeepTransactionId,
      submittedAt: operation.submittedAt ?? null,
      updatedAt: operation.updatedAt ?? null,
    }));
  storage.setItem(persistenceKey(sessionKey), JSON.stringify(references));
};

export const loadPersistedOperationReferences = (storage, sessionKey) => {
  const raw = storage.getItem(persistenceKey(sessionKey));
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((entry) => entry?.operationId && entry?.operationType)
      : [];
  } catch {
    return [];
  }
};

export const clearPersistedOperationReferences = (storage, sessionKey) => {
  storage.removeItem(persistenceKey(sessionKey));
};

export const attachConsentTransactionId = (operation, transactionId) => {
  if (operation.executionMode !== "METAKEEP_BUSINESS_WALLET") {
    throw new Error("MetaKeep transaction IDs can only attach to MetaKeep operations");
  }
  if (!transactionId) {
    throw new Error("transactionId is required");
  }
  return {
    ...operation,
    metakeepTransactionId: transactionId,
    state: "SUBMITTED",
  };
};

export const attachDirectTransactionHash = (operation, transactionHash) => {
  if (operation.executionMode !== "DIRECT_WALLET") {
    throw new Error("direct transaction hashes can only attach to direct operations");
  }
  if (!transactionHash) {
    throw new Error("transactionHash is required");
  }
  return {
    ...operation,
    transactionHash,
    state: "SUBMITTED",
  };
};

export { EXECUTION_MODES, OPERATION_STATES };
